/**
 * =========================================================================================
 *     Copyright © 2026 Dobler Data Solutions, LLC. ALL RIGHTS RESERVED.
 * =========================================================================================
 *     File            :   useTenantJobSchedule.ts
 *     Feature         :   Run Now is acknowledged at once and cannot be sent twice (owner, 2026-10-31)
 *     Description     :   The one read of what the organization's jobs are doing, shared by the job cards and the
 *                         Schedules tab so a Run Now on one shows on the other. A job on its way polls faster,
 *                         because its state is what the person is waiting to see change.
 *     Author          :   Peter Dobler
 * =========================================================================================
 */
import { useQuery, type QueryClient } from '@tanstack/react-query';

import { getJobSchedules, type JobRunState, type TenantJobSchedule } from '../../services/jobService';

export const TENANT_JOB_SCHEDULE_KEY = ['tenant-job-schedule'] as const;

const ACTIVE_POLL_MS = 10_000;
const IDLE_POLL_MS = 30_000;

export const RUN_STATE_LABEL: Record<JobRunState, string> = {
  requested: 'Request received',
  queued: 'Waiting for your worker',
  running: 'Running',
};

export const RUN_STATE_HINT: Record<JobRunState, string> = {
  requested: 'Your request is in. It goes to your worker next.',
  queued: 'Your worker starts this as soon as it is free. It runs your jobs one at a time.',
  running: 'This job is running now.',
};

export const pollIntervalFor = (data: TenantJobSchedule | undefined): number =>
  Object.keys(data?.job_states ?? {}).length > 0 ? ACTIVE_POLL_MS : IDLE_POLL_MS;

export function useTenantJobSchedule() {
  return useQuery({
    queryKey: TENANT_JOB_SCHEDULE_KEY,
    queryFn: getJobSchedules,
    refetchInterval: (query) => pollIntervalFor(query.state.data),
  });
}

/** The server has accepted a run: show it now, then let what the server says replace this. */
export function markRunRequested(queryClient: QueryClient, jobName: string): void {
  queryClient.setQueryData<TenantJobSchedule>(TENANT_JOB_SCHEDULE_KEY, (current) =>
    current && {
      ...current,
      job_states: { ...current.job_states, [jobName]: 'requested' },
      schedules: current.schedules.map((s) => (s.job_name === jobName ? { ...s, run_state: 'requested' } : s)),
    },
  );
  void queryClient.invalidateQueries({ queryKey: TENANT_JOB_SCHEDULE_KEY });
}
